import React, { useState } from 'react';
import { Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "react-oidc-context"
import '../styles/index.css';
import { claimValueContains, getClaimValue, hasRoleClaim } from "../utils/authUser";
import LimeClaimTypes from "../models/limeClaimTypes";
import UserType from "../models/userType";
import OrganisationUserType from "../models/organisationUserType";
import WellKnownRoles from "../models/wellKnownRoles";
import { User } from "oidc-client-ts";
import NavBar from "./Navigation";
import Footer from "./Footer";
import Header from "./Header";
import CookieBanner from "./Shared/CookieBanner/CookieBanner";
import WelcomeJourney from "./Welcome";
import { limeApiFetch, limeApiGet } from "../utils/api";
import { sleepFor } from "../utils/async";
import LoadingSpinner from "./Shared/LoadingSpinner/LoadingSpinner";
import { patchCreateElementForCspNonce, observeStyleElementsForCspNonce } from "../utils/cspNonce";

export interface GetUserTerms {
  accepted: boolean
}

function App() {
  const navigate = useNavigate();
  const auth = useAuth();
  const [acceptedTerms, setAcceptedTerms] = useState<boolean | null>(null);
  const duplicateSessionCheckFrequency = 60000 // 60 seconds

  async function acceptTerms() {
    for (let i = 0; i < 3; i++) {
      try {
        if (i > 0) {
          await sleepFor(i);
        }
        const orgUuid = getClaimValue(auth.user, LimeClaimTypes.OrganisationUuid);
        const termsResponse = await limeApiFetch(`organisations/${orgUuid}/terms`, 'POST', auth);
        if (termsResponse.ok) {
          setAcceptedTerms(true);
          return true;
        } else {
          console.log("Accept terms failed due to unexpected response status: " + termsResponse.status);
        }
      } catch (e) {
        console.error("An error occurred while attempting to accept the terms", e);
      }
    }
    setAcceptedTerms(true); // Allow the user to continue
    return false;
  }

  function setSessionInfo() {
    if (auth.user && 'expires_at' in auth.user) {
      const sessionInfo = {
        expires_at: auth.user.expires_at,
        email: auth.user.profile.email
      };
      sessionStorage.setItem("sessionInfo", JSON.stringify(sessionInfo));
      localStorage.setItem("sessionInfo", JSON.stringify(sessionInfo));
    } else {
      console.error("Unable to set session info. User or expiration timestamp missing.");
    }
  }

  React.useEffect(() => {
    function checkLogoutSignal() {
      const localSessionInfo = JSON.parse(localStorage.getItem("sessionInfo") || '{}');
      const currentSessionInfo = JSON.parse(sessionStorage.getItem("sessionInfo") || '{}');
      const emailChanged = localSessionInfo.email !== currentSessionInfo.email;
      if (localSessionInfo.expires_at && currentSessionInfo.expires_at && localSessionInfo.expires_at > currentSessionInfo.expires_at && emailChanged) {
        window.sessionStorage.clear();
        window.location.reload();
      }
    }
    const intervalId = setInterval(checkLogoutSignal, duplicateSessionCheckFrequency);
    window.addEventListener("popstate", checkLogoutSignal);

    function handleStorageEvent(e: StorageEvent) {
      if (e.key === "sessionInfo") {
        checkLogoutSignal();
      }
    }
    window.addEventListener("storage", handleStorageEvent);

    return () => {
      clearInterval(intervalId);
      window.removeEventListener("popstate", checkLogoutSignal);
      window.removeEventListener("storage", handleStorageEvent);
    };
  }, []);



  React.useEffect(() => {
    (async () => {
      if (auth.isLoading) {
        return;
      }

      if (auth.error) {
        await auth.signoutRedirect();
        return;
      }

      if (!auth.isAuthenticated) {
        await auth.signinRedirect({ state: window.location.pathname });
      }
      else if (window.location.pathname.startsWith('/impersonate/')) {
        navigate(window.location.pathname, { replace: true });
      }
      else {
        let currentSessionInfo = JSON.parse(sessionStorage.getItem("sessionInfo") || '{}');
        if (!currentSessionInfo.expires_at) {
          setSessionInfo();
        }

        let isOrganisationUser = (user: User | null | undefined) => {
          return getClaimValue(user, LimeClaimTypes.UserType) === UserType.Organisation &&
            getClaimValue(user, LimeClaimTypes.OrganisationUuid) &&
            claimValueContains(user, LimeClaimTypes.OrganisationUserType, OrganisationUserType.Admin)
        };

        let isGlobalAdmin = (user: User | null | undefined) => {
          return getClaimValue(user, LimeClaimTypes.UserType) === UserType.Lime &&
            hasRoleClaim(user, WellKnownRoles.LimeAdmin)
        };

        if (isOrganisationUser(auth.user) || isGlobalAdmin(auth.user)) {
          const orgUuid = getClaimValue(auth.user, LimeClaimTypes.OrganisationUuid);
          if (!orgUuid) {
            navigate("/access-denied", { replace: true });
          }

          if (window.location.pathname === "" || window.location.pathname === "/") {
            navigate("/dashboard", { replace: true }); // Redirect straight into the dashboard when entering the default host url
          } else {
            navigate(window.location.pathname, { replace: true })
          }
        } else {
          navigate("/access-denied", { replace: true });
        }
      }
    })();
  }, [auth.isLoading]);

  React.useEffect(() => {
    (async () => {
      if (!auth.isLoading && auth.isAuthenticated) {
        const orgUuid = getClaimValue(auth.user, LimeClaimTypes.OrganisationUuid);
        if (orgUuid) {
          try {
            const terms = await limeApiGet<GetUserTerms>(`organisations/${orgUuid}/terms`, auth);
            setAcceptedTerms(terms.accepted);
          } catch (error) {
            console.error("Failed to fetch user terms:", error);
            setAcceptedTerms(false);
          }
        }
      }
    })();
  }, [auth.isLoading, auth.isAuthenticated]);

  // Initialize CSP nonce handling for dynamically created styles (e.g. Tailwind, dev tooling, SDKs)
  React.useEffect(() => {
    const restoreCreateElement = patchCreateElementForCspNonce();
    const disconnectObserver = observeStyleElementsForCspNonce();

    return () => {
      restoreCreateElement();
      disconnectObserver();
    };
  }, []);

  const impersonating = window.location.pathname.startsWith('/impersonate/');

  if (auth.isLoading || (!impersonating && acceptedTerms == null)) {
    return (
        <div className="my-xl">
            <LoadingSpinner loading={true}>&nbsp;</LoadingSpinner>
        </div>
    );
  }

  if (acceptedTerms || impersonating) {
    return (
        <div className="h-full">
          <CookieBanner/>
          <div className="relative bg-pure-white container mx-auto flex min-h-full flex-col">
            <header className="w-full shrink-0 px-md sm:px-xl pt-md sm:pt-xl pb-sm">
              <Header/>
            </header>
            <div className="flex min-h-0 flex-1 flex-col sm:flex-row">
              <nav className="shrink-0 bg-pure-white px-md py-sm border-b border-b-light-grey sm:w-[16.25rem] sm:border-b-0 sm:border-r sm:border-portal-teal/25">
                <NavBar/>
              </nav>
              <main className="min-h-[25rem] flex-1 bg-background-blu p-md sm:px-xl sm:pb-xl sm:pt-md">
                <Outlet/>
              </main>
            </div>
            <footer className="mt-[50px] w-full shrink-0 bg-portal-header-wash">
              <Footer/>
            </footer>
          </div>
        </div>
    )
  } else {
    return (
        <div>
          <CookieBanner/>
          <WelcomeJourney onAcceptTerms={acceptTerms}/>
        </div>
    )
  }
}

export default App;